<div class="row page-header">
    <div class="col-12">
        <h1>
            Safety Dashboard
            <small class="text-muted">High-level summary of safety metrics</small>
        </h1>
    </div>
</div>

@if (isInitialLoading) {
    <div class="text-center py-5">
        <div class="spinner-border text-primary mb-3" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
        <p class="text-muted mb-0">Loading safety metrics...</p>
    </div>
}

<div [hidden]="isInitialLoading">
    @if (loadError) {
        <div class="alert alert-warning py-2 small" role="alert">
            Some safety metrics could not be loaded. Try refreshing the page.
        </div>
    }

    <div class="row g-3">
        <div class="col-12 col-lg-4">
            <div class="card h-100">
                <div class="card-body d-flex flex-column">
                    <h5 class="card-title">Job Hazard Analysis</h5>
                    <div class="row g-2 mb-3">
                        <div class="col-6">
                            <div class="text-muted small text-uppercase">JHAs submitted (30 days)</div>
                            <div class="fs-4 fw-semibold">{{ summary.jha.jhasSubmittedLast30Days }}</div>
                        </div>
                        <div class="col-6">
                            <div class="text-muted small text-uppercase">Open non-compliant items</div>
                            <div class="fs-4 fw-semibold">{{ summary.jha.openNonCompliantItems }}</div>
                        </div>
                        <div class="col-6">
                            <div class="text-muted small text-uppercase">Jobs due for JHA</div>
                            <div class="fs-4 fw-semibold text-danger">{{ summary.jha.jobsDueCount }}</div>
                        </div>
                    </div>
                    @if (canViewJhaDashboard) {
                        <a routerLink="/safety/job-hazard-analysis" class="mt-auto align-self-start">View dashboard</a>
                    }
                </div>
            </div>
        </div>

        <div class="col-12 col-lg-4">
            <div class="card h-100">
                <div class="card-body d-flex flex-column">
                    <h5 class="card-title">Vehicle Inspections</h5>
                    <div class="row g-2 mb-3">
                        <div class="col-6">
                            <div class="text-muted small text-uppercase">Inspections this month</div>
                            <div class="fs-4 fw-semibold">{{ summary.vehicleInspection.inspectionsThisMonth }}</div>
                        </div>
                        <div class="col-6">
                            <div class="text-muted small text-uppercase">Vehicles overdue</div>
                            <div class="fs-4 fw-semibold text-danger">{{ summary.vehicleInspection.vehiclesOverdueThisMonth }}</div>
                        </div>
                    </div>
                    @if (canViewVehicleInspectionDashboard) {
                        <a routerLink="/safety/vehicle-inspection" class="mt-auto align-self-start">View dashboard</a>
                    }
                </div>
            </div>
        </div>

        <div class="col-12 col-lg-4">
            <div class="card h-100">
                <div class="card-body d-flex flex-column">
                    <h5 class="card-title">Subcontractor Training</h5>
                    <div class="row g-2 mb-3">
                        <div class="col-6">
                            <div class="text-muted small text-uppercase">Total sent</div>
                            <div class="fs-4 fw-semibold">{{ summary.subcontractorTraining.totalSent }}</div>
                        </div>
                        <div class="col-6">
                            <div class="text-muted small text-uppercase">Completed</div>
                            <div class="fs-4 fw-semibold text-success">{{ summary.subcontractorTraining.completed }}</div>
                        </div>
                        <div class="col-6">
                            <div class="text-muted small text-uppercase">Pending</div>
                            <div class="fs-4 fw-semibold">{{ summary.subcontractorTraining.pending }}</div>
                        </div>
                        <div class="col-6">
                            <div class="text-muted small text-uppercase">Overdue</div>
                            <div class="fs-4 fw-semibold text-danger">{{ summary.subcontractorTraining.overdue }}</div>
                        </div>
                    </div>
                    @if (canViewSubcontractorTraining) {
                        <a routerLink="/safety/subcontractor-training" class="mt-auto align-self-start">View dashboard</a>
                    }
                </div>
            </div>
        </div>

        <div class="col-12 col-lg-4">
            <div class="card h-100">
                <div class="card-body d-flex flex-column">
                    <h5 class="card-title">Safety Feedback</h5>
                    <div class="row g-2 mb-3">
                        <div class="col-6">
                            <div class="text-muted small text-uppercase">Total submitted</div>
                            <div class="fs-4 fw-semibold">{{ summary.safetyFeedback.totalSubmitted }}</div>
                        </div>
                        <div class="col-6">
                            <div class="text-muted small text-uppercase">Submitted this month</div>
                            <div class="fs-4 fw-semibold">{{ summary.safetyFeedback.submittedThisMonth }}</div>
                        </div>
                        <div class="col-6">
                            <div class="text-muted small text-uppercase">Submitted last 30 days</div>
                            <div class="fs-4 fw-semibold">{{ summary.safetyFeedback.submittedLast30Days }}</div>
                        </div>
                        <div class="col-6">
                            <div class="text-muted small text-uppercase">Jobs with feedback</div>
                            <div class="fs-4 fw-semibold">{{ summary.safetyFeedback.jobsWithFeedback }}</div>
                        </div>
                    </div>
                    @if (summary.safetyFeedback.topJobs.length) {
                        <div class="mb-3">
                            <div class="text-muted small text-uppercase mb-1">Top jobs by feedback</div>
                            <ul class="list-unstyled mb-0">
                                @for (job of summary.safetyFeedback.topJobs; track job.jobId) {
                                    <li class="d-flex justify-content-between border-bottom py-1">
                                        <span class="text-truncate me-2">{{ job.jobName }}</span>
                                        <span class="fw-semibold">{{ job.count }}</span>
                                    </li>
                                }
                            </ul>
                        </div>
                    }
                    @if (canViewSafetyFeedback) {
                        <a routerLink="/safety/safety-feedback" class="mt-auto align-self-start">View records</a>
                    }
                </div>
            </div>
        </div>

        <div class="col-12 col-lg-4">
            <div class="card h-100">
                <div class="card-body d-flex flex-column">
                    <h5 class="card-title">Feedback Requiring JHA</h5>
                    <div class="row g-2 mb-3">
                        <div class="col-6">
                            <div class="text-muted small text-uppercase">Needs JHA</div>
                            <div class="fs-4 fw-semibold text-danger">{{ summary.feedbackRequiringJha.needsJhaCount }}</div>
                        </div>
                        <div class="col-6">
                            <div class="text-muted small text-uppercase">JHA in progress</div>
                            <div class="fs-4 fw-semibold">{{ summary.feedbackRequiringJha.inProgressCount }}</div>
                        </div>
                        <div class="col-6">
                            <div class="text-muted small text-uppercase">Addressed this month</div>
                            <div class="fs-4 fw-semibold text-success">{{ summary.feedbackRequiringJha.addressedThisMonth }}</div>
                        </div>
                    </div>
                    @if (summary.feedbackRequiringJha.outstanding.length) {
                        <div class="mb-3">
                            <div class="text-muted small text-uppercase mb-1">Awaiting a JHA</div>
                            <ul class="list-unstyled mb-0">
                                @for (item of summary.feedbackRequiringJha.outstanding; track item.feedbackId) {
                                    <li class="d-flex justify-content-between border-bottom py-1">
                                        <span class="text-truncate me-2">{{ item.jobName }}</span>
                                        <span class="fw-semibold text-nowrap">{{ item.eventDateTime | date: 'M/d/yy' }}</span>
                                    </li>
                                }
                            </ul>
                        </div>
                    }
                    @if (canViewSafetyFeedback) {
                        <a routerLink="/safety/safety-feedback" class="mt-auto align-self-start">View records</a>
                    }
                </div>
            </div>
        </div>
    </div>
</div>
